<script lang="ts" setup>
const route = useRoute();

const isCatalogRoute = computed(
  () => route.path === '/' || route.path.startsWith('/catalog') || route.path.startsWith('/tags'),
);

const tagSlugs = computed<string[]>(() => {
  // /tags/[...slugs]
  const slugsParam = route.params.slugs;
  if (slugsParam) {
    const joined = Array.isArray(slugsParam) ? slugsParam.join(',') : String(slugsParam);
    return joined ? joined.split(',') : [];
  }
  // /catalog/[...categories]/tags/[...slugs]
  return parseCatalogPath(route.params.categories).tagSlugs;
});

const { data: commonData } = await useAsyncCommonData();

const { data: mainPage } = useAsyncData('catalog-main', () => CommonAPI.main(), { dedupe: 'defer' });

const applyApprovedFilter = useArticleApprovedFilter();

const { data: articles } = useAsyncData(
  'catalog-articles',
  () =>
    applyApprovedFilter(
      queryCollection('blog')
        .order('order', 'ASC')
        .select('path', 'title', 'image', 'category', 'sortAfterProduct', 'sortAfterProductFallback')
        .where('section_media', '=', 1)
        .where('category', '=', 'articles'),
    ).all(),
  { dedupe: 'defer' },
);

const categoryCode = computed(() => {
  const { sectionSegments } = parseCatalogPath(route.params.categories);
  if (!sectionSegments.length) return undefined;
  return sectionSegments[sectionSegments.length - 1];
});

const products = computed(() =>
  commonData.value.products.filter((el) => !el.secret).toSorted((a, b) => (a.sortMain ?? 500) - (b.sortMain ?? 500)),
);
</script>

<template>
  <div class="indexLayout" :class="{ indexLayout_noMdFooter: route.meta.noMdFooter }">
    <div class="indexLayout__container">
      <HomeCatalogView
        v-if="isCatalogRoute && mainPage && articles"
        :category-code="categoryCode"
        :active-tag-slugs="tagSlugs"
        :banners="mainPage.banners"
        :news="mainPage.news"
        :articles="articles"
        :products="products"
        :sections="commonData.sections"
      />
      <NuxtPage v-else class="indexLayout__content" />
    </div>

    <LazyVisibleLayoutFooter hydrate-on-visible class="indexLayout__footer" />
  </div>
</template>

<style lang="scss">
.indexLayout {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;

  &_noMdFooter &__footer {
    @include mq('md') {
      display: none;
    }
  }

  &__container {
    display: contents;
  }

  &__container:empty + &__footer,
  &__container:has(&__content:empty) + &__footer {
    opacity: 0;
  }

  &__footer {
    margin-top: auto;
    transition: opacity 0.3s ease;
  }
}
</style>
